import type {
  TableProps as ChakraTableProps,
  ThemingProps,
} from '@chakra-ui/react';
import { Table as ChakraTable } from '@chakra-ui/react';
import React, { useCallback, useMemo, useState } from 'react';

import { useEvent } from '../../hooks/useEvent';
import type { SortOrder } from '../../types';
import type { OmitChakraProps } from '../../utils/typeUtils';

import type { TableContext } from './Table.context';
import { TableProvider } from './Table.context';

interface SortProperties<TSortBy extends string | number> {
  sortOrder: SortOrder;
  sortBy: TSortBy;
}

export interface TableProps<TSortBy extends string | number = string | number>
  extends Omit<OmitChakraProps<ChakraTableProps, keyof ThemingProps>, 'as'> {
  isInteractive?: boolean;
  variant?: 'plain' | 'striped';
  size?: 'condensed' | 'default';
  sortOrder?: SortOrder;
  sortBy?: TSortBy;
  defaultSortBy?: TSortBy;
  defaultSortOrder?: SortOrder;
  onRequestSort?: (
    sortProperties: SortProperties<TSortBy>,
    event: React.MouseEvent,
  ) => void;
  ref?: React.Ref<HTMLTableElement>;
}

export function Table<TSortBy extends string | number = string | number>({
  isInteractive: interactive,
  sortOrder,
  sortBy,
  defaultSortOrder = 'asc',
  defaultSortBy,
  onRequestSort = () => {},
  ref,
  ...props
}: TableProps<TSortBy>) {
  const handleSort = useEvent(onRequestSort);
  const [sortOrderState, setSortOrderState] = useState(defaultSortOrder);
  const [sortByState, setSortByState] = useState(defaultSortBy);

  const setSort = useCallback(
    (
      attribute: string | number,
      nextSortOrder: SortOrder,
      event: React.MouseEvent,
    ) => {
      if (sortOrder === undefined) {
        setSortOrderState(nextSortOrder);
      }

      if (sortBy === undefined) {
        setSortByState(attribute as TSortBy);
      }

      handleSort(
        { sortOrder: nextSortOrder, sortBy: attribute as TSortBy },
        event,
      );
    },
    [sortOrder, sortBy, handleSort],
  );

  const context = useMemo<TableContext>(
    () => ({
      sortOrder: sortOrder === undefined ? sortOrderState : sortOrder,
      sortBy: sortBy === undefined ? sortByState : sortBy,
      setSort,
    }),
    [sortOrder, sortOrderState, sortBy, sortByState, setSort],
  );

  return (
    <TableProvider value={context}>
      <ChakraTable ref={ref} data-interactive={interactive} {...props} />
    </TableProvider>
  );
}
